import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';

import {
    obtenerEntidades,
    registrarEntidadId,
    eliminarUsuario,
    actualizarEntidadUsuarioId
} from '../../Services/serviciohttp';
import { API_URLS } from '../../Services/servicioURL';

const Usuarios: React.FC = () => {
    // State for form data
    const [formData, setFormData] = useState({
        id: '',
        nombre: '',
        usuario: '',
        contraseña: '',
        telefono: ''
    });

    // Mode state for controlling form behavior
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const toast = useRef<Toast>(null);

    // Filtered data using useMemo for better performance
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const searchTermLower = searchTerm.toLowerCase();
        return data.filter(item =>
            String(item.id).toLowerCase().includes(searchTermLower) ||
            String(item.nombre).toLowerCase().includes(searchTermLower) ||
            String(item.usuario).toLowerCase().includes(searchTermLower) ||
            (item.telefono && String(item.telefono).toLowerCase().includes(searchTermLower))
        );
    }, [data, searchTerm]);

    // Función para procesar el número de teléfono
    const procesarTelefono = (telefono: string): string => {
        if (!telefono) return telefono;
        
        const numeroLimpio = telefono.replace(/[\s\-\(\)]/g, '');
        
        if (numeroLimpio.startsWith('1')) {
            return numeroLimpio;
        }
        
        if (numeroLimpio.length === 10 && /^\d{10}$/.test(numeroLimpio)) {
            return '1' + numeroLimpio;
        }
        
        return numeroLimpio;
    };

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        if (name === 'telefono') {
            const telefonoProcesado = procesarTelefono(value);
            setFormData(prev => ({ ...prev, [name]: telefonoProcesado }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Get mode indicator
    const getModeIndicator = () => {
        switch (mode) {
            case 'new':
                return { text: 'NUEVO USUARIO', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO USUARIO', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    // Obtain users for consultation
    const obtenerUsuarios = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.usuarios.obtener);
            setData(result);
        } catch (error) {
            console.error('Error al obtener usuarios:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar los datos',
                life: 3000
            });
        }
    };

    // Effect to load data when table is shown
    useEffect(() => {
        if (mostrarTabla) {
            obtenerUsuarios();
        }
    }, [mostrarTabla]);

    // New button handler
    const handleNuevo = () => {
        setFormData({
            id: '',
            nombre: '',
            usuario: '',
            contraseña: '',
            telefono: ''
        });
        setMode('new');
    };

    // Edit button handler
    const handleEditar = () => {
        if (!formData.id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un usuario para editar',
                life: 3000
            });
            return;
        }
        setMode('edit');
    };

    // Save button handler
    const handleGuardar = async () => {
        try {
            if (!formData.nombre || !formData.usuario || !formData.contraseña) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Campos requeridos',
                    detail: 'Debe completar todos los campos obligatorios',
                    life: 3000
                });
                return;
            }

            if (mode === 'new') {
                await registrarEntidadId(
                    API_URLS.usuarios.registrar, 
                    '', 
                    formData.nombre, 
                    formData.usuario, 
                    formData.contraseña,
                    formData.telefono
                );
                
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'Usuario registrado exitosamente',
                    life: 3000
                });
            } else if (mode === 'edit') {
                await actualizarEntidadUsuarioId(
                    API_URLS.usuarios.actualizar(formData.id), 
                    formData.id, 
                    formData.nombre,
                    formData.usuario,
                    formData.contraseña,
                    formData.telefono
                );
                
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Actualizado!',
                    detail: 'Usuario modificado exitosamente',
                    life: 3000
                });
            }

            setFormData({
                id: '',
                nombre: '',
                usuario: '',
                contraseña: '',
                telefono: ''
            });
            setMode('view');
            obtenerUsuarios();
        } catch (error) {
            console.error('Error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar el usuario',
                life: 3000
            });
        }
    };

    // Delete button handler
    const handleEliminar = async () => {
        if (!formData.id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un usuario para eliminar',
                life: 3000
            });
            return;
        }

        try {
            const confirmacion = window.confirm('⚠️ ¿Estás seguro de que deseas eliminar este usuario?\n\nEsta acción no se puede deshacer.');
            if (!confirmacion) return;

            await eliminarUsuario(API_URLS.usuarios.eliminar(formData.id));
            
            toast.current?.show({
                severity: 'success',
                summary: '¡Eliminado!',
                detail: 'Usuario eliminado exitosamente',
                life: 3000
            });

            setFormData({
                id: '',
                nombre: '',
                usuario: '',
                contraseña: '',
                telefono: ''
            });
            setMode('view');
            obtenerUsuarios();
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar el usuario',
                life: 3000
            });
        }
    };

    // Cancel button handler
    const handleCancelar = () => {
        setFormData({
            id: '',
            nombre: '',
            usuario: '',
            contraseña: '',
            telefono: ''
        });
        setMode('view');
    };

    // Open consultation table
    const handleConsultar = () => {
        setMostrarTabla(true);
    };

    // Close consultation table
    const cerrarTabla = () => {
        setMostrarTabla(false);
        setSearchTerm('');
    };

    // Select user from consultation table
    const handleSeleccionarRegistro = (registro: any) => {
        setFormData({
            id: registro.id,
            nombre: registro.nombre,
            usuario: registro.usuario,
            contraseña: registro.contraseña || '',
            telefono: registro.telefono || ''
        });
        setMostrarTabla(false);
        setMode('view');
        setSearchTerm('');
    };

    return (
        <div>
            <Navbar />
            <Sidebar />
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                <Toast ref={toast} />

                {/* Modal de la tabla mejorado */}
                {mostrarTabla && (
                    <div className="overlay" onClick={cerrarTabla} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '900px',
                            width: '90%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}>
                            {/* Header fijo */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                    👥 Consultar Usuarios
                                </h3>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded"
                                    onClick={cerrarTabla}
                                    style={{ color: '#6b7280' }}
                                />
                            </div>

                            {/* Barra de búsqueda fija */}
                            <div style={{ marginBottom: '20px', flexShrink: 0 }}>
                                <div style={{ position: 'relative' }}>
                                    <i className="pi pi-search" style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#9ca3af',
                                        fontSize: '0.9rem'
                                    }}></i>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            paddingLeft: '40px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '0.95rem'
                                        }}
                                        placeholder="Buscar por ID, nombre, usuario o teléfono..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Contador de resultados fijo */}
                            <div style={{ 
                                marginBottom: '15px', 
                                fontSize: '0.9em', 
                                color: '#6b7280',
                                padding: '8px 12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                flexShrink: 0
                            }}>
                                📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> registros
                            </div>

                            {/* Contenedor de tabla con scroll */}
                            <div style={{ 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Header de tabla fijo */}
                                <div style={{ 
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '2px solid #e5e7eb',
                                    flexShrink: 0
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ 
                                                    padding: '12px 16px', 
                                                    textAlign: 'left', 
                                                    fontWeight: '600', 
                                                    color: '#374151',
                                                    width: '15%'
                                                }}>🆔 ID</th>
                                                <th style={{ 
                                                    padding: '12px 16px', 
                                                    textAlign: 'left', 
                                                    fontWeight: '600', 
                                                    color: '#374151',
                                                    width: '30%'
                                                }}>👤 Nombre</th>
                                                <th style={{ 
                                                    padding: '12px 16px', 
                                                    textAlign: 'left', 
                                                    fontWeight: '600', 
                                                    color: '#374151',
                                                    width: '30%'
                                                }}>🔑 Usuario</th>
                                                <th style={{ 
                                                    padding: '12px 16px', 
                                                    textAlign: 'left', 
                                                    fontWeight: '600', 
                                                    color: '#374151',
                                                    width: '25%'
                                                }}>📞 Teléfono</th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>
                                
                                {/* Cuerpo de tabla con scroll */}
                                <div style={{ 
                                    flex: 1,
                                    overflowY: 'auto',
                                    maxHeight: 'calc(80vh - 200px)'
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {filteredData.length > 0 ? (
                                                filteredData.map((item, index) => (
                                                    <tr 
                                                        key={index} 
                                                        onClick={() => handleSeleccionarRegistro(item)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                    >
                                                        <td style={{ 
                                                            padding: '12px 16px', 
                                                            borderBottom: '1px solid #f3f4f6',
                                                            width: '15%'
                                                        }}>{item.id}</td>
                                                        <td style={{ 
                                                            padding: '12px 16px', 
                                                            borderBottom: '1px solid #f3f4f6',
                                                            width: '30%'
                                                        }}>{item.nombre}</td>
                                                        <td style={{ 
                                                            padding: '12px 16px', 
                                                            borderBottom: '1px solid #f3f4f6',
                                                            width: '30%'
                                                        }}>{item.usuario}</td>
                                                        <td style={{ 
                                                            padding: '12px 16px', 
                                                            borderBottom: '1px solid #f3f4f6',
                                                            width: '25%'
                                                        }}>{item.telefono || 'Sin teléfono'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} style={{ 
                                                        padding: '40px', 
                                                        textAlign: 'center',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        No se encontraron resultados
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Formulario principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '900px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Header con indicador de modo */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                👥 Usuarios
                            </h2>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                backgroundColor: modeInfo.color,
                                color: 'white',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                            }}>
                                <i className={`pi ${modeInfo.icon}`}></i>
                                {modeInfo.text}
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                            {/* Primera fila: ID y Nombre */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🆔 ID
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            backgroundColor: '#f9fafb'
                                        }}
                                        name="id"
                                        value={formData.id}
                                        onChange={handleInputChange}
                                        disabled
                                        placeholder="Auto-generado"
                                    />
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        👤 Nombre <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        disabled={mode === 'view'}
                                        placeholder="Ingrese el nombre completo"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Segunda fila: Usuario y Teléfono */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🔑 Usuario <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        name="usuario"
                                        value={formData.usuario}
                                        onChange={handleInputChange}
                                        disabled={mode === 'view'}
                                        placeholder="Nombre de usuario"
                                        required
                                    />
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        📞 Teléfono
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleInputChange}
                                        disabled={mode === 'view'}
                                        placeholder="Ej: 6683670737"
                                        maxLength={15}
                                    />
                                    {mode !== 'view' && formData.telefono && (
                                        <small style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                                            Se guardará como: {procesarTelefono(formData.telefono)}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Tercera fila: Contraseña */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🔒 Contraseña <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        name="contraseña"
                                        value={formData.contraseña}
                                        onChange={handleInputChange}
                                        disabled={mode === 'view'}
                                        type="password"
                                        placeholder="Contraseña del usuario"
                                        required
                                    />
                                </div>
                                <div>
                                    {/* Campo vacío para mantener el layout */}
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Botones reorganizados */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button
                                    icon="pi pi-search"
                                    label="Consultar"
                                    className="p-button-info p-button-raised"
                                    onClick={handleConsultar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-plus"
                                    label="Nuevo"
                                    className="p-button-success p-button-raised"
                                    onClick={handleNuevo}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-pencil"
                                    label="Editar"
                                    className="p-button-warning p-button-raised"
                                    onClick={handleEditar}
                                    disabled={!formData.id}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px',
                                        opacity: !formData.id ? 0.5 : 1
                                    }}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    label="Eliminar"
                                    className="p-button-danger p-button-raised"
                                    onClick={handleEliminar}
                                    disabled={!formData.id}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px',
                                        opacity: !formData.id ? 0.5 : 1
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    icon="pi pi-check"
                                    label="Guardar"
                                    className="p-button-success p-button-raised"
                                    onClick={handleGuardar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    label="Cancelar"
                                    className="p-button-secondary p-button-raised"
                                    onClick={handleCancelar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Usuarios;